import { EvercycleBuybackRecommendationDetails, Incident, RecommendationCode } from '@pure1/data';
import {
    EvercycleBuybackRenderInfo,
    EvercycleExcludedComponent,
    EvercycleExclusionReason,
    EvercycleProgram,
    EvercycleScope,
    EvercycleValuation,
} from '../../../../data/models/recommendations.models';
import { EVERCYCLE_BUYBACK_CARD_PLUGIN_NAME, EVERCYCLE_BUYBACK_DIALOG_PLUGIN_NAME } from './evercycle-plugin-names';
import { EVERCYCLE_DOCUMENTATION_URL, EVERCYCLE_EXCLUSION_COPY, EVERCYCLE_PROGRAM_LABEL } from './evercycle-copy';

/*
 * A plain function rather than part of RecommendationProcessingService so surfaces outside the
 * recommendations container (row badges, renewal modals) can build the same render info without
 * pulling in that service's authorization dependency.
 */

/**
 * Builds the EverCycle Buyback card and dialog render info from a buyback incident.
 *
 * Strict where the incident parse is lenient: it throws, and callers skip the item and log the
 * reason, when the buyback block is missing, the scope or an exclusion reason
 * is outside the vocabulary the card and dialog can display, or an offer is present but
 * incomplete. A partial offer is rejected rather than shown as "Valuation pending" because the
 * two are different states — pending means no offer exists yet — and conflating them would
 * hide an upstream contract break behind a plausible-looking card.
 */
/** Exclusion reasons the dialog has copy for; any other reason is rejected rather than shown blank. */
const KNOWN_EXCLUSION_REASONS: ReadonlySet<string> = new Set(Object.keys(EVERCYCLE_EXCLUSION_COPY));

export function buildEvercycleBuybackRenderInfo(incident: Incident): EvercycleBuybackRenderInfo {
    const details = incident.getEvercycleBuybackDetails();
    if (!details.hasBuybackBlock) {
        throw new Error(`${EVERCYCLE_PROGRAM_LABEL} incident ${incident.id} has no buyback block`);
    }
    if (incident.orgId == null) {
        throw new Error(`${EVERCYCLE_PROGRAM_LABEL} incident ${incident.id} has no org id`);
    }
    if (!details.entityName) {
        throw new Error(`${EVERCYCLE_PROGRAM_LABEL} incident ${incident.id} has no target name`);
    }

    return new EvercycleBuybackRenderInfo({
        cardPluginComponentName: EVERCYCLE_BUYBACK_CARD_PLUGIN_NAME,
        dialogPluginComponentName: EVERCYCLE_BUYBACK_DIALOG_PLUGIN_NAME,
        id: incident.id,
        targetName: details.entityName,
        recommendationHeader: {
            label: EVERCYCLE_PROGRAM_LABEL,
            emphasis: 'success',
        },
        program: toEvercycleProgram(incident.recommendationCode),
        orgId: String(incident.orgId),
        recommendationId: incident.id,
        scope: toEvercycleScope(incident.id, details.buybackScope),
        excludedComponents: toEvercycleExcludedComponents(incident.id, details),
        valuation: toEvercycleValuation(incident.id, details),
        documentationUrl: EVERCYCLE_DOCUMENTATION_URL,
        subscriptionExpiredAtUtc: details.subscriptionExpiredAt,
    });
}

function toEvercycleProgram(code: RecommendationCode): EvercycleProgram {
    switch (code) {
        case RecommendationCode.EvercycleBuybackFA:
            return 'evercycle-buyback-fa';
        case RecommendationCode.EvercycleBuybackFB:
            return 'evercycle-buyback-fb';
        default:
            throw new Error(`Recommendation code ${code} is not an ${EVERCYCLE_PROGRAM_LABEL} program`);
    }
}

function toEvercycleScope(incidentId: string, scope: string | null): EvercycleScope {
    if (scope === 'FULL_APPLIANCE' || scope === 'COMPONENT_ONLY') {
        return scope;
    }
    throw new Error(`${EVERCYCLE_PROGRAM_LABEL} incident ${incidentId} has unsupported buyback_scope "${scope}"`);
}

/** Excluded components become the dialog's fine print; included ones are not listed. */
function toEvercycleExcludedComponents(
    incidentId: string,
    details: EvercycleBuybackRecommendationDetails,
): EvercycleExcludedComponent[] {
    return details.components
        .filter(component => component.buybackEligible === false)
        .map(component => {
            const reason = component.exclusionReason;
            if (!reason || !KNOWN_EXCLUSION_REASONS.has(reason)) {
                throw new Error(
                    `${EVERCYCLE_PROGRAM_LABEL} incident ${incidentId} has unsupported exclusion_reason "${reason}"`,
                );
            }
            const label = [component.categoryDisplay, component.model].filter(Boolean).join(' ');
            if (!label) {
                throw new Error(
                    `${EVERCYCLE_PROGRAM_LABEL} incident ${incidentId} has an excluded component with no label`,
                );
            }
            return { label, reason: reason as EvercycleExclusionReason };
        });
}

function toEvercycleValuation(
    incidentId: string,
    details: EvercycleBuybackRecommendationDetails,
): EvercycleValuation | null {
    const offer = details.valuation;
    if (!offer) {
        return null;
    }
    if (!offer.offerId || !offer.currency || offer.totalAmount == null || !offer.validUntil) {
        throw new Error(`${EVERCYCLE_PROGRAM_LABEL} incident ${incidentId} has an incomplete valuation`);
    }
    return {
        offerId: offer.offerId,
        totalAmount: offer.totalAmount,
        currency: offer.currency,
        totalAmountBucket: bucketEvercycleTotalAmount(offer.totalAmount),
        validUntilUtc: offer.validUntil,
        actionableUntilUtc: offer.actionableUntil ?? undefined,
    };
}

/**
 * Coarse amount band for analytics, so events never carry the exact offer value and the
 * property stays low-cardinality. Bands are in the offer's own currency units.
 */
export function bucketEvercycleTotalAmount(totalAmount: number): EvercycleValuation['totalAmountBucket'] {
    if (totalAmount < 10_000) {
        return '<10k';
    }
    if (totalAmount < 50_000) {
        return '10k-50k';
    }
    if (totalAmount <= 100_000) {
        return '50k-100k';
    }
    return '>100k';
}
